import React, { useState } from 'react';
import { ProjectPermissionToggle } from '@/components/permissions';
import { faDesktop, faEdit, faMoneyCheckEdit, faTrashAlt, faCalendarDay, faList, faLink, faExternalLink } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';
import { ResidentialUnit } from '@/types';
import { ConfirmDeleteDialog } from '@/components/dialogs';
import ExtraCostsDialog from '@/seller/components/extra-costs/ExtraCostsDialog';
import DeadlineEditDialog from '@/seller/components/deadline';
import { deleteResidentialUnit } from '@/services/residential-units';
import { useProjectRelativeRedirect } from '@/utils/hooks';
import { CardDropdownMenu } from '@/components/Cards';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import ResidentialUnitInfoDialog from './ResidentialUnitInfoDialog';
import { ResidentialUnitEdit } from './ResidentialUnitEdit';
import { getModuleUrl } from '@/utils/returnUrl';
import { BygrModule } from '@ygtechnologies/bygr-frontend-component-library';
import { ResidentialUnitDeleteDialog } from './ResidentialUnitDeleteDialog';
import { Dropdown, DropdownContextMenu } from '@ygtechnologies/rubus-ui';
import { Toast } from '@ygtechnologies/bygr-frontend-component-library';

interface ResidentialUnitContextMenuProps {
    residentialUnit: ResidentialUnit;
    onDelete?(): void;
    onUpdate(): void;
    inCard?: boolean;
}

const ResidentialUnitContextMenu: React.FC<ResidentialUnitContextMenuProps> = ({
    residentialUnit,
    onDelete,
    onUpdate,
    inCard = false,
}) => {
    const [showExtraCostsDialog, setShowExtraCostsDialog] = useState(false);

    const [showEditDeadlinesDialog, setShowEditDeadlinesDialog] = useState(false);

    const [showUnitEditDialog, setShowUnitEditDialog] = useState(false);
    const [showUnitInfoDialog, setShowUnitInfoDialog] = useState(false);

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const renderDropDownList = () => {
        return (
            <>
                <ProjectPermissionToggle condition="canViewAsBuyer">
                    <Dropdown.Item
                        as="a"
                        href={
                            getModuleUrl(BygrModule.ChangeManagement, window.APP_CONFIG) +
                            `/impersonate/${residentialUnit.id}`
                        }
                        target="_blank"
                        icon={faDesktop}>
                        <Trans>View as buyer</Trans>
                    </Dropdown.Item>
                </ProjectPermissionToggle>

                <ProjectPermissionToggle condition={(p) => !p.canManageResidentialUnits}>
                    <Dropdown.Item
                        icon={faList}
                        onClick={(e) => {
                            setShowUnitInfoDialog(true);
                        }}>
                        <Trans>View unit info</Trans>
                    </Dropdown.Item>
                </ProjectPermissionToggle>

                {residentialUnit.showroomPath && (() => {
                    // BASE_URL is '/cm/' (vite config) — already starts and ends with '/'.
                    const showroomUrl = `${window.location.origin}${import.meta.env.BASE_URL}showroom/${residentialUnit.showroomPath}`;
                    return (
                        <>
                            <Dropdown.Item
                                as="a"
                                href={showroomUrl}
                                target="_blank"
                                icon={faExternalLink}>
                                <Trans>Open showroom</Trans>
                            </Dropdown.Item>
                            <Dropdown.Item
                                icon={faLink}
                                onClick={async () => {
                                    try {
                                        await navigator.clipboard.writeText(showroomUrl);
                                        Toast.success(<Trans>Showroom URL copied to clipboard</Trans>);
                                    } catch {
                                        Toast.error(<Trans>Could not copy URL to clipboard</Trans>);
                                    }
                                }}>
                                <Trans>Copy showroom link</Trans>
                            </Dropdown.Item>
                        </>
                    );
                })()}

                <ProjectPermissionToggle condition="canManageResidentialUnits">
                    <Dropdown.Item
                        icon={faEdit}
                        onClick={(e) => {
                            setShowUnitEditDialog(true);
                        }}>
                        <Trans>Edit unit</Trans>
                    </Dropdown.Item>

                    <Dropdown.Item
                        onClick={() => {
                            setShowExtraCostsDialog(true);
                        }}
                        icon={faMoneyCheckEdit}>
                        <Trans>Add extra costs</Trans>
                    </Dropdown.Item>
                    <Dropdown.Item
                        onClick={() => {
                            setShowEditDeadlinesDialog(true);
                        }}
                        icon={faCalendarDay}>
                        <Trans>Edit deadlines</Trans>
                    </Dropdown.Item>
                    <Dropdown.Item
                        onClick={() => {
                            setShowDeleteDialog(true);
                        }}
                        icon={faTrashAlt}>
                        <Trans>Delete unit</Trans>
                    </Dropdown.Item>
                </ProjectPermissionToggle>
            </>
        );
    };

    const Wrapper = inCard ? CardDropdownMenu : DropdownContextMenu;

    return (
        <>
            <Wrapper>{renderDropDownList()}</Wrapper>
            <ResidentialUnitDeleteDialog
                residentialUnit={residentialUnit}
                show={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                onDeleted={onDelete}
                redirectAfterDelete={true}
            />

            <ExtraCostsDialog
                residentialUnit={residentialUnit}
                show={showExtraCostsDialog}
                onClose={() => setShowExtraCostsDialog(false)}
                onAccept={() => null}
            />

            <DeadlineEditDialog
                onClose={() => setShowEditDeadlinesDialog(false)}
                onAccept={() => setShowEditDeadlinesDialog(false)}
                residentialUnitId={residentialUnit.id}
                show={showEditDeadlinesDialog}
            />
            <ResidentialUnitEdit
                // projectId={residentialUnit.projectId}
                residentialUnitId={residentialUnit.id}
                show={showUnitEditDialog}
                // onClose={() => setShowUnitEditDialog(false)}
                onSave={onUpdate}
                toggleOpen={() => setShowUnitEditDialog(false)}
            />
            {/* <ResidentialUnitEditDialog
                projectId={residentialUnit.projectId}
                residentialUnitId={residentialUnit.id}
                show={showUnitEditDialog}
                onClose={() => setShowUnitEditDialog(false)}
                onSave={onUpdate}
            /> */}

            <ResidentialUnitInfoDialog
                projectId={residentialUnit.projectId}
                residentialUnitId={residentialUnit.id}
                show={showUnitInfoDialog}
                onClose={() => setShowUnitInfoDialog(false)}
            />
        </>
    );
};

export default ResidentialUnitContextMenu;
